// Stockage local chiffré (cadrage, section 9) : AES-GCM 256 bits par WebCrypto, clé aléatoire propre à la
// session conservée dans le stockage de session de l'application (perdue à la fermeture : les données
// locales deviennent illisibles, ce qui borne leur exposition).

export interface StockageCle {
  getItem(cle: string): string | null;
  setItem(cle: string, valeur: string): void;
  removeItem(cle: string): void;
}

export const CLE_SESSION = 'ct-cle-locale';

function versBase64(octets: Uint8Array): string {
  let s = '';
  for (const o of octets) s += String.fromCharCode(o);
  return btoa(s);
}

function depuisBase64(texte: string): Uint8Array {
  const s = atob(texte);
  const octets = new Uint8Array(s.length);
  for (let i = 0; i < s.length; i++) octets[i] = s.charCodeAt(i);
  return octets;
}

export interface Chiffre {
  iv: string;
  donnees: string;
}

/** Contenu binaire chiffré (photos) : conservé tel quel dans IndexedDB, sans encodage texte. */
export interface ChiffreBinaire {
  iv: Uint8Array;
  donnees: ArrayBuffer;
}

export class CoffreLocal {
  private cle: CryptoKey | null = null;

  constructor(
    private readonly stockage: StockageCle,
    private readonly sousCrypto: SubtleCrypto = globalThis.crypto.subtle,
  ) {}

  private async obtenirCle(): Promise<CryptoKey> {
    if (this.cle) return this.cle;
    let brute = this.stockage.getItem(CLE_SESSION);
    if (!brute) {
      brute = versBase64(globalThis.crypto.getRandomValues(new Uint8Array(32)));
      this.stockage.setItem(CLE_SESSION, brute);
    }
    this.cle = await this.sousCrypto.importKey('raw', depuisBase64(brute) as BufferSource, { name: 'AES-GCM' }, false, ['encrypt', 'decrypt']);
    return this.cle;
  }

  async chiffrer(valeur: unknown): Promise<Chiffre> {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const clair = new TextEncoder().encode(JSON.stringify(valeur));
    const chiffre = await this.sousCrypto.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, await this.obtenirCle(), clair as BufferSource);
    return { iv: versBase64(iv), donnees: versBase64(new Uint8Array(chiffre)) };
  }

  async dechiffrer<T>(c: Chiffre): Promise<T> {
    const clair = await this.sousCrypto.decrypt(
      { name: 'AES-GCM', iv: depuisBase64(c.iv) as BufferSource },
      await this.obtenirCle(),
      depuisBase64(c.donnees) as BufferSource,
    );
    return JSON.parse(new TextDecoder().decode(clair)) as T;
  }

  /** Chiffrement d'un contenu binaire (photo) : le chiffré est conservé tel quel, sans encodage texte. */
  async chiffrerOctets(octets: Uint8Array): Promise<ChiffreBinaire> {
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(12));
    const chiffre = await this.sousCrypto.encrypt({ name: 'AES-GCM', iv: iv as BufferSource }, await this.obtenirCle(), octets as BufferSource);
    return { iv, donnees: chiffre };
  }

  async dechiffrerOctets(c: ChiffreBinaire): Promise<Uint8Array> {
    const clair = await this.sousCrypto.decrypt({ name: 'AES-GCM', iv: c.iv as BufferSource }, await this.obtenirCle(), c.donnees);
    return new Uint8Array(clair);
  }

  /** Oublie la clé (déconnexion) : les données locales chiffrées deviennent illisibles. */
  oublier(): void {
    this.cle = null;
    this.stockage.removeItem(CLE_SESSION);
  }
}
